import { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  FormControl,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Aggregations from '../../../../elements/Aggregation/AggregationsTable';
import { AggregationColumn, AggregationItem, JiraTicket } from 'common-types';
import {
  MdExpandLess as ExpandLessIcon,
  MdExpandMore as ExpandMoreIcon,
} from 'react-icons/md';
import { debounce } from 'lodash';
const AggregationsMemo = memo(Aggregations);

const CreateJiraTicketDescriptionFields: FC<
  CreateJiraTicketDescriptionFieldsProps
> = ({
  jiraTicketFields,
  onJiraTicketFormChange,
  selectedAggregationsProps,
}) => {
  const scrollRef = useRef(null);
  const [collapse, setCollapse] = useState(false);
  const toggleuDescription = useCallback(() => {
    setCollapse(!collapse);
  }, [collapse]);

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const elem = scrollRef.current as HTMLDivElement;
      collapse &&
        debounce(() => {
          elem.scrollIntoView({
            behavior: 'smooth',
            inline: 'nearest',
          });
        }, 300)();
    }
  }, [collapse]);

  return (
    <Stack ref={scrollRef} component='div' spacing={2} direction='column'>
      <Box>
        <Button
          fullWidth={false}
          onClick={toggleuDescription}
          endIcon={collapse ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ textTransform: 'none' }}
          variant='text'>
          <Typography variant='caption' color='inherit'>
            View / Edit Ticket Description
          </Typography>
        </Button>
      </Box>
      <Collapse in={collapse}>
        <Stack direction='column' spacing={2}>
          <FormControl size='small' fullWidth>
            <TextField
              inputProps={{
                'data-testid': 'app-name',
              }}
              size='small'
              defaultValue={jiraTicketFields.description?.appName}
              onChange={onJiraTicketFormChange}
              name='appName'
              label='Application Name'
            />
          </FormControl>
          <FormControl size='small' fullWidth>
            <TextField
              inputProps={{
                'data-testid': 'category',
              }}
              size='small'
              defaultValue={jiraTicketFields.description?.category}
              onChange={onJiraTicketFormChange}
              name='category'
              label='Category'
            />
          </FormControl>
          <FormControl size='small' fullWidth>
            <TextField
              inputProps={{
                'data-testid': 'policy-name',
              }}
              size='small'
              defaultValue={jiraTicketFields.description?.policyName}
              onChange={onJiraTicketFormChange}
              name='policyName'
              label='Policy Name'
            />
          </FormControl>
          {jiraTicketFields.description?.fixRecommendation && (
            <FormControl size='small' fullWidth>
              <TextField
                inputProps={{
                  'data-testid': 'recommendation',
                }}
                size='small'
                defaultValue={jiraTicketFields.description?.fixRecommendation}
                onChange={onJiraTicketFormChange}
                name='fixRecommendation'
                label='Recommendation'
              />
            </FormControl>
          )}
          {jiraTicketFields.description?.fixLink && (
            <FormControl size='small' fullWidth>
              <TextField
                size='small'
                defaultValue={jiraTicketFields.description.fixLink}
                onChange={onJiraTicketFormChange}
                name='fixLink'
                label='Fix Link'
              />
            </FormControl>
          )}
          {selectedAggregationsProps && (
            <AggregationsMemo
              withSelection={false}
              {...selectedAggregationsProps}
            />
          )}
        </Stack>
      </Collapse>
    </Stack>
  );
};

export interface CreateJiraTicketDescriptionFieldsProps {
  onJiraTicketFormChange: (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>,
  ) => void;
  selectedAggregationsProps?: {
    info: string;
    config: { withSelection: boolean; columns: AggregationColumn[] };
    items: AggregationItem[];
  };
  jiraTicketFields: JiraTicket;
}

export default CreateJiraTicketDescriptionFields;
